import { useEffect, useRef } from "react";
import cloneDeep from "lodash/cloneDeep";
import { PAGE_TYPE } from "../types/pages.type";
import { ADT_TABLE_TYPE } from "../types/common.type";
import { setCardFilter } from "../store/reducers/admission.slice";
import { setCardFilter as setCardFilterComparison } from "../store/reducers/comparisonReducers/admissionComparison.slice";
import { readPageConverterCardContextForPageType } from "./page-converter-navigation.service";

const CONVERTER_SOURCE_PAGES = new Set([
	PAGE_TYPE.HOSPITAL,
	PAGE_TYPE.COMMUNITY_TRANSFER,
	PAGE_TYPE.DECEASED,
	PAGE_TYPE.OVERALL,
]);

/** Dashboard cardFilter priority `type` → ADT child `id` (admission.data OTHER_ADT_OPTIONS). */
const HOSPITAL_PRIORITY_TO_ADT_CHILD = {
	total: ADT_TABLE_TYPE.ALL,
	hospitalizations: "reHospitalization",
	unplannedHospitalTransfer: "unplannedHospitalTransfer",
	plannedHospitalTransfer: "plannedHospitalTransfer",
	DCERData: "wasAdmitted",
	insuranceData: "insuranceId",
	returnsData: "wasReturned",
	ninetyDaysData: "90Days",
	floorsData: "floorId",
	doctorData: "doctorId",
	daysData: "day",
	dxData: "dx",
	shiftData: "shiftName",
	nurseData: "nurseId",
	hospitalData: "hospitalId",
};

const COMMUNITY_PRIORITY_TO_ADT_CHILD = {
	total: ADT_TABLE_TYPE.ALL,
	safeDischarge: "safeDischarge",
	SNF: "SNF",
	AMA: "AMA",
	insuranceData: "insuranceId",
	sixtyDaysData: "60Days",
	returnsData: "wasReturned",
	doctorData: "doctorId",
	floorsData: "floorId",
	daysData: "day",
	safeDischargeAssLivData: "assistantLivId",
	snfFacilityData: "snfFacilityId",
};

const DECEASED_PRIORITY_TO_ADT_CHILD = {
	total: ADT_TABLE_TYPE.ALL,
	insuranceData: "insuranceId",
	doctorData: "doctorId",
	floorsData: "floorId",
	daysData: "day",
	ninetyDaysData: "90DaysDeceased",
};

/** Overall dashboard `priorityData` `type` → ADT child id (`OTHER_ADT_OPTIONS` Overall). */
const OVERALL_PRIORITY_TO_ADT_CHILD = {
	total: ADT_TABLE_TYPE.ALL,
	totalIncoming: ADT_TABLE_TYPE.ALL,
	totalOutgoing: ADT_TABLE_TYPE.ALL,
	insuranceData: "insuranceId",
	doctorData: "doctorId",
	floorsData: "floorId",
	daysData: "day",
	ninetyDaysData: "90DaysOverall",
};

function mapForSourcePage(sourcePage) {
	if (sourcePage === PAGE_TYPE.HOSPITAL) return HOSPITAL_PRIORITY_TO_ADT_CHILD;
	if (sourcePage === PAGE_TYPE.COMMUNITY_TRANSFER) return COMMUNITY_PRIORITY_TO_ADT_CHILD;
	if (sourcePage === PAGE_TYPE.DECEASED) return DECEASED_PRIORITY_TO_ADT_CHILD;
	if (sourcePage === PAGE_TYPE.OVERALL) return OVERALL_PRIORITY_TO_ADT_CHILD;
	return null;
}

export function normalizePriorityFilterType(type) {
	if (typeof type !== "string") return type;
	return type.replace(/_spacial$/i, "");
}

export function mapPriorityTypeToAdtChildId(sourcePage, priorityType) {
	const map = mapForSourcePage(sourcePage);
	if (!map) return null;
	const key = normalizePriorityFilterType(priorityType);
	return map[key] ?? null;
}

/**
 * @param {string} sourcePage PAGE_TYPE
 * @param {Array<{ type?: string }>} priorityData from persisted page-converter card context
 * @param {Array<{ id: string }>} parentChildren ADT children for that source tab
 * @returns {Set<string>}
 */
export function resolveSelectedAdtChildIds(sourcePage, priorityData, parentChildren) {
	const validIds = new Set((parentChildren || []).map((c) => c.id));
	/** Total row for Hospital / Community / Deceased ADT tabs (`OTHER_ADT_OPTIONS`). */
	if (!priorityData?.length) {
		if (validIds.has(ADT_TABLE_TYPE.ALL)) {
			return new Set([ADT_TABLE_TYPE.ALL]);
		}
		return new Set();
	}
	const picked = new Set();
	for (const entry of priorityData) {
		if (!entry?.type) continue;
		const mapped = mapPriorityTypeToAdtChildId(sourcePage, entry.type);
		if (mapped && validIds.has(mapped)) {
			picked.add(mapped);
		}
		const direct = normalizePriorityFilterType(entry.type);
		if (direct && validIds.has(direct)) {
			picked.add(direct);
		}
	}
	return picked;
}

export function buildPageConverterAdtParentEntry(parentOption, selectedChildIds) {
	const children = (parentOption.children || []).map((ch) => ({
		...ch,
		isSelected: selectedChildIds.has(ch.id),
	}));
	const hasAny = children.some((c) => c.isSelected);
	return {
		...parentOption,
		children,
		...(hasAny ? { isSelected: true } : {}),
	};
}

function stableChildrenKey(parentOption) {
	if (!parentOption?.children?.length) return "";
	return parentOption.children.map((c) => c.id).join(",");
}

/**
 * After page-converter navigation: select the matching transfer-status tab + ADT rows (total only if priority is empty; otherwise only rows matching priority), then run transfer status.
 */
export function usePageConverterAdtAutomation({
	pageConverterSourcePage,
	otherAdtOptionsWithCustom,
	loading,
	dispatch,
	handleTransferStatusRun,
}) {
	const didRunRef = useRef(false);
	const runRef = useRef(handleTransferStatusRun);
	runRef.current = handleTransferStatusRun;

	const optionsRef = useRef(otherAdtOptionsWithCustom);
	optionsRef.current = otherAdtOptionsWithCustom;

	const parentOpt = otherAdtOptionsWithCustom?.find((o) => o.id === pageConverterSourcePage);
	const childrenKey = stableChildrenKey(parentOpt);

	useEffect(() => {
		if (didRunRef.current) return;
		if (!pageConverterSourcePage || !CONVERTER_SOURCE_PAGES.has(pageConverterSourcePage)) return;
		if (loading) return;

		const parent = optionsRef.current?.find((o) => o.id === pageConverterSourcePage);
		if (!parent?.children?.length) return;

		const context = readPageConverterCardContextForPageType(pageConverterSourcePage);
		const priorityData = Array.isArray(context?.priorityData) ? context.priorityData : [];

		const selectedIds = resolveSelectedAdtChildIds(
			pageConverterSourcePage,
			priorityData,
			parent.children
		);
		const entry = buildPageConverterAdtParentEntry(parent, selectedIds);
		if (!entry.children.some((c) => c.isSelected)) return;

		dispatch(setCardFilter({ adtData: [entry] }));
		dispatch(setCardFilterComparison({ adtData: [cloneDeep(entry)] }));

		didRunRef.current = true;

		const timer = window.setTimeout(() => {
			void runRef.current();
		}, 0);
		return () => window.clearTimeout(timer);
	}, [pageConverterSourcePage, loading, childrenKey, dispatch]);
}
